import { SECTION_SLUGS } from "@shared/constants/sections";
import { TOP_ZONE_SCAFFOLDS } from "@shared/constants/top-zone-scaffolds";
import { INTERVIEW_CHAPTERS } from "@shared/interview/chapters";
import { HOME_BOARD, RULED_EXECUTOR_WORDS, chapterTitle, fill } from "./planBoard";

/**
 * s237 (ACROSS-YOUR-PLAN-1 PR B) — WHERE THINGS ARE, the planner's view: the rows the server returns
 * (`GET /api/clients/:id/where-things-are`, PR A's fixed registry), named and grouped the way board 12 draws them.
 * Pure — the page renders what these return.
 */

/** One row as PR A's endpoint returns it. */
export interface WhereRow {
  sectionSlug: string;
  sectionId: string;
  categoryId: string | null;
  fieldKey: string;
  fieldId: string;
  rowId: string | null;
  recordLabel: string;
  location: string | null;
  missing: boolean;
  catA: boolean;
  onNoPage: boolean;
  /** A valuables row that is a firearm — asked on the Firearms screen (D-237.57 item 4). */
  firearm?: boolean;
  typedColumn: { kind: string; col: string } | null;
}

/**
 * What each registry source is called, and the board page it is asked on (chapter number + board 12's screen name).
 * `what: null` — the record names itself (a valuable, an ID, a paper); `chapter: null` — on no chapter (the secure
 * file's pieces, board 8, not built). 🔴 The item words not on board 12 (all but House keys, Car keys, Car papers and
 * Home safe) are words in code, owed to the board (reported s237).
 */
const S = SECTION_SLUGS;
export const SOURCE_WORDS: Readonly<Record<string, { what: string | null; chapter: number | null; screen: string }>> = {
  [`${S.ASSETS_PROPERTIES}.key_location`]: { what: "House keys", chapter: 2, screen: "Home and property" },
  [`${S.ASSETS_PROPERTIES}.mailbox_key_location`]: { what: "Mailbox key", chapter: 2, screen: "Home and property" },
  [`${S.ASSETS_PROPERTIES}.safe_location`]: { what: "Home safe", chapter: 2, screen: "Home and property" },
  [`${S.ASSETS_PROPERTIES}.safe_combination`]: { what: "Home safe combination", chapter: 2, screen: "Home and property" },
  [`${S.FINANCIAL_AND_INSURANCE_HOUSING}.key_location`]: { what: "Keys to a place you rent", chapter: 2, screen: "Home and property" },
  [`${S.ASSETS_VEHICLE_LIST}.key_location`]: { what: "Car keys", chapter: 2, screen: "Vehicles" },
  [`${S.ASSETS_VEHICLE_LIST}.vehicle_location`]: { what: "Where it is parked", chapter: 2, screen: "Vehicles" },
  [`${S.ASSETS_VEHICLE_LIST}.papers_location`]: { what: "Car papers", chapter: 2, screen: "Vehicles" },
  [`${S.ASSETS_VALUABLES}.location`]: { what: null, chapter: 2, screen: "Valuables" },
  [`${S.ASSETS_SAFES_LOCKED_PLACES}.location`]: { what: null, chapter: 2, screen: "Safes" },
  [`${S.ASSETS_SAFES_LOCKED_PLACES}.key_location`]: { what: "Its key", chapter: 2, screen: "Safes" },
  [`${S.LEGAL_IMPORTANT_DOCUMENTS}.location`]: { what: null, chapter: 5, screen: "Where your papers are" },
  [`${S.LEGAL_POWERS_OF_ATTORNEY}.location`]: { what: null, chapter: 5, screen: "Powers of attorney" },
  [`${S.LEGAL_BUSINESS_DOCUMENTS}.location`]: { what: null, chapter: 5, screen: "Business papers" },
  [`${S.PERSONAL_INFORMATION_GOVERNMENT_IDS}.location`]: { what: null, chapter: 1, screen: "Government ID" },
  [`${S.PASSWORD_LOG_DEVICES_LOCAL_ACCESS}.location`]: { what: null, chapter: null, screen: "Sensitive information" },
  [`${S.PROTECT_YOUR_ACCESS}.secure_file_location`]: { what: "Your secure file", chapter: null, screen: "Sensitive information" },
  [`${S.INSTRUCTIONS_FOR_EXECUTORS_WHERE_ARE_THEY}.location`]: { what: null, chapter: null, screen: "" },
};

/** s237 (D-237.57 item 3): an unnamed record's "Which one" — a vehicle with no make or model reads "Your vehicle", the
 *  vehicle starter row's own name (top-zone-scaffolds.ts), never a blank. */
const UNNAMED: Readonly<Record<string, string>> = { [S.ASSETS_VEHICLE_LIST]: TOP_ZONE_SCAFFOLDS[S.ASSETS_VEHICLE_LIST]?.[0] ?? "" };

/** A firearm is a valuables row, asked on the Firearms screen: its trail and its page are Firearms, not Valuables. */
const sourceOf = (r: Pick<WhereRow, "sectionSlug" | "fieldKey" | "firearm">) => {
  const src = SOURCE_WORDS[`${r.sectionSlug}.${r.fieldKey}`];
  return src && r.firearm ? { ...src, screen: "Firearms" } : src;
};

/** "What" and "Which one": the item's own word with its record beside it, or the record alone. */
export function whatAndWhich(r: WhereRow): { what: string; which: string } {
  const src = sourceOf(r);
  if (src?.what) return { what: src.what, which: r.recordLabel || UNNAMED[r.sectionSlug] || "" };
  // A record with no name yet is called by its page (board 12's screen name), never left blank.
  return { what: r.recordLabel || src?.screen || "", which: "" };
}

/** The row's trail (board 12: "<chapter> → <screen>"), or "Things on no page". */
export function trailOf(r: WhereRow): string {
  if (r.onNoPage) return HOME_BOARD.where.noPage.name;
  const src = sourceOf(r);
  if (!src) return "";
  return src.chapter ? `${chapterTitle(src.chapter)} → ${src.screen}` : src.screen;
}

/** The BUILT interview screen this row is asked on (its key), or undefined — then there is nowhere to open. */
export function screenKeyOf(r: WhereRow): string | undefined {
  const src = sourceOf(r);
  if (!src?.chapter) return undefined;
  const chapter = INTERVIEW_CHAPTERS.find((c) => c.number === src.chapter);
  return chapter?.screens.find((s) => s.kind === "question" && s.crumb === src.screen)?.key;
}

/** "Saved to <trail>", or "Kept here, in your plan" for a thing on no page. */
export function landsOn(r: WhereRow, executor?: { planner: string }): string {
  if (!r.onNoPage) return fill(HOME_BOARD.where.savedTo, { trail: trailOf(r) });
  // D-237.65: the executor reads "Kept here, in <planner>'s plan".
  return executor ? fill(RULED_EXECUTOR_WORDS.keptHere, { planner: executor.planner }) : HOME_BOARD.where.keptHere;
}

/** The rows the page shows: search over what, which one and the place; "Only what is missing" keeps the blanks. */
export function filterRows(rows: WhereRow[], opts: { query: string; missingOnly: boolean }): WhereRow[] {
  const q = opts.query.trim().toLowerCase();
  return rows.filter((r) => {
    if (opts.missingOnly && !r.missing) return false;
    if (!q) return true;
    const { what, which } = whatAndWhich(r);
    return `${what} ${which} ${r.location ?? ""}`.toLowerCase().includes(q);
  });
}

/** Grouped by page, in chapter order, Things on no page last (board 12 groups by the trail). */
export function groupRows(rows: WhereRow[]): { trail: string; rows: WhereRow[] }[] {
  const order = (r: WhereRow) => (r.onNoPage ? 99 : sourceOf(r)?.chapter ?? 98);
  const groups = new Map<string, { trail: string; rank: number; rows: WhereRow[] }>();
  for (const r of rows) {
    const t = trailOf(r);
    const g = groups.get(t) ?? { trail: t, rank: order(r), rows: [] };
    g.rows.push(r);
    groups.set(t, g);
  }
  return Array.from(groups.values()).sort((a, b) => a.rank - b.rank).map(({ trail, rows: rs }) => ({ trail, rows: rs }));
}

/**
 * The "+ Add" dropdown (D-237.55): board 12's kinds of thing, each with where it goes. A kind whose screen is BUILT opens
 * it (the way back returns to Where things are); every other kind — the board's "added here" kinds, and one whose screen
 * is not built yet — is added here, in Things on no page, named after the kind (the board's own rule for a kind with no
 * screen).
 */
export function addKinds(): { label: string; where: string; screenKey: string | undefined }[] {
  const built = (name: string) => {
    for (const c of INTERVIEW_CHAPTERS) {
      const s = c.screens.find((x) => x.kind === "question" && x.crumb === name);
      if (s) return s.key;
    }
    return undefined;
  };
  return HOME_BOARD.where.add.kinds.map((k) => {
    const screenKey = k.where === HOME_BOARD.where.add.addedHere ? undefined : built(k.where);
    return { label: k.label, where: screenKey ? k.where : HOME_BOARD.where.add.addedHere, screenKey };
  });
}
